import formset, { loadOptions } from './formset.js';
import { makeOptions, getTopIndex } from './record.js';
import { replaceIndex, REGEX } from './table.js';
import field from './field.js';
import db from './db.js';
import api from '../js/api.js';
import filter_form from './forms/filter.js';
import { setupFilterCsvImport } from './filter_csv.js';

const DATALIST = /(<datalist\s+id="fields"[^>]*>)(.*?)(<\/datalist>)/ims;
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Armazéns ativos para UI (filtros, datalists) — cadastro de armazéns não usa esta lista local. */
function warehousesActiveOnly(rows) {
	return (Array.isArray(rows) ? rows : []).filter(
		(r) => !(r.is_disabled === true || r.is_disabled === 1 || r.is_disabled === '1')
	);
}

/** Extrai company_id do formulário de filtro (resolve nick→id se necessário). Usado para escopar todos os campos filhos da empresa. */
const getCompanyIdFromFormData = async function(formData, top, companies = null) {
	companies = companies ?? await db.list('companies').catch(() => []);
	for (let i = 0; i < top; i++) {
		if (formData[`field-${i}`] === 'company_id' && formData[`value-${i}`]) {
			const val = formData[`value-${i}`];
			if (UUID_REGEX.test(String(val))) return val;
			const c = companies.find(x => (x.nick || '') === val || (x.name || '') === val || String(x.nick || '') === val || String(x.name || '') === val);
			return c?.id ?? null;
		}
	}
	return null;
};

// 'this' is not standard in these instances of formInit() and formCheck() as they point to the 'filter' object instead of a 'form' object.
// The formset creation requires a descriptor and such descriptor must be modified beforehand to point to our local 'init' and 'check' replacements.
// So these descriptor functions are bound to the 'filter' object which will then hold a pointer to the formset after its creation.
const formInit = function()
{
	// MT-596E375: inscrever para carregar opções ao trocar campo ou ao alterar Empresa (para atualizar Armazém)
	const form_node = this.formset.form.node || document.querySelector('#filter');
	if (form_node)
		form_node.addEventListener('change', (ev) =>
		{
			const fieldMatch = ev.target?.id?.match(/^field-(\d+)$/);
			if (fieldMatch)
			{
				const field = this.formset.form.getFieldBySelector(ev.target.id);
				if (field)
					updateTypes.call(this, ev, { selector: ev.target.id }, field.getValue());
			}
			else
			{
				const valueMatch = ev.target?.id?.match(/^value-(\d+)$/);
				if (valueMatch)
				{
					const idx = valueMatch[1];
					const fieldEl = this.formset.form.getFieldBySelector?.('field-' + idx);
					const fn = fieldEl?.getValue?.();
					if (fn === 'company_id')
					{
						// Empresa alterada: recarregar opções de TODOS os campos filhos (Armazém, Posição, Programa, etc.)
						const formData = this.formset?.form?.getValues?.() || {};
						const refreshAllCompanyScoped = () =>
						{
							const top = 10;
							for (let i = 0; i < top; i++)
							{
								const f = this.formset.form.getFieldBySelector?.('field-' + i);
								const fName = f?.getValue?.();
								if (fName && fName !== 'company_id' && this.fields?.some(s => s.field === fName && s.store))
									updateTypes.call(this, ev, { selector: 'field-' + i }, fName);
							}
						};
						if (this.reloadLocations)
							this.reloadLocations(formData).then(refreshAllCompanyScoped).catch(() => {});
						else
							refreshAllCompanyScoped();
					}
					else if (fn === 'warehouse_id')
					{
						// Armazém alterado: recarregar locations da API e opções de Posição
						const formData = this.formset?.form?.getValues?.() || {};
						if (this.reloadLocations)
							this.reloadLocations(formData).then(() =>
							{
								const top = 10;
								for (let i = 0; i < top; i++)
								{
									const f = this.formset.form.getFieldBySelector?.('field-' + i);
									if (f?.getValue?.() === 'location_id')
										updateTypes.call(this, ev, { selector: 'field-' + i }, 'location_id');
								}
							}).catch(() => {});
						else
						{
							const top = 10;
							for (let i = 0; i < top; i++)
							{
								const f = this.formset.form.getFieldBySelector?.('field-' + i);
								if (f?.getValue?.() === 'location_id')
									updateTypes.call(this, ev, { selector: 'field-' + i }, 'location_id');
							}
						}
					}
				}
			}
		});
	Object.getPrototypeOf(this.formset.form).init.call(this.formset.form, this.formset);
};

const activeFilterIndices = function(form, indices)
{
	return indices.filter(i =>
	{
		const f = form.getFieldBySelector(`field-${i}`);
		return f && !f.isDisabled() && !f.isEmpty();
	});
};

const formCheck = function()
{
	let indices = this.formset.getEnabledRowsIndices();
	const active = activeFilterIndices(this.formset.form, indices);
	if (active.length === 0)
	{
		Object.getPrototypeOf(this.formset.form).check.call(this.formset.form, this.formset);
		return;
	}
	checkParens.call(this.formset.form, indices);
	checkValues.call(this.formset.form, active);
	checkConnectors.call(this.formset.form, active);
	Object.getPrototypeOf(this.formset.form).check.call(this.formset.form, this.formset);
};

const checkParens = function(indices)
{
	let balance = 0;
	indices.forEach(index =>
	{
		const openField = this.getFieldBySelector(`open-${index}`);
		const closeField = this.getFieldBySelector(`close-${index}`);
		balance += Number(openField?.getValue?.() ?? 0) || 0;
		balance -= Number(closeField?.getValue?.() ?? 0) || 0;
		if (balance < 0)
			throw new Error('Falta algum parêntese de abertura');
	});
	if (balance > 0)
		throw new Error('Falta algum parêntese de fechamento');
};

const checkValues = function(indices)
{
	indices.forEach(index =>
	{
		let field = this.getFieldBySelector(`field-${index}`);
		let operator = this.getFieldBySelector(`operator-${index}`);
		let value = this.getFieldBySelector(`value-${index}`);
		if (field.isDisabled() || field.isEmpty())
			return;
		if (!operator.isDisabled() && operator.isEmpty())
			setErrorState(operator, `Verificar operador da linha ${index+1}`);
		if (!value.isDisabled() && (value.isEmpty() || !value.checkValue()) && !['nu', 'nn'].includes(operator.getValue()))
			setErrorState(value, `Verificar dado da linha ${index+1}`);
	});
};

const checkConnectors = function(activeIndices)
{
	activeIndices.forEach((index, ai) =>
	{
		let field = this.getFieldBySelector(`connector-${index}`);
		let value = field.getValue();
		let is_last_active = (ai === activeIndices.length - 1);
		if ((!is_last_active && value != ',' && value != ';') || (is_last_active && value !== undefined && value != '' && value != -1))
			setErrorState(field, `Verificar conector da linha ${index+1}`);
	});
};

const setErrorState = function(field, message)
{
	field.setErrorState(true);
	throw new Error(message);
};

const fmtLocAddr = a => Array.isArray(a) ? a.join('-') : String(a ?? '');

/** ILIKE em location_address — nunca expandir location_id para warehouse_id+location_id com co/bw/ew (ILIKE '%2%' casa 2,12,20…). */
const LOCATION_TEXT_FILTER_OPS = ['co', 'bw', 'ew', 'nb', 'nd', 'nc'];

/** "2 - ZZ-99" ou "2-ZZ-99" → núcleo do endereço para casar com array_to_string(address,'-') na API */
function stripWarehousePrefixFromLocationSearch(v)
{
	const s = String(v ?? '').trim();
	const m = s.match(/^(\d+)\s*-\s*(.+)$/);
	const core = m ? m[2].trim() : s;
	return core.replace(/\s+/g, '-');
}

const resolveFilterIdsToLabels = async function(data, fieldspecs)
{
	if (!data || !fieldspecs)
		return data;
	const [companies, locations] = await Promise.all([
		db.list('companies').catch(() => []),
		db.list('locations').catch(() => [])
	]);
	const top = getTopIndex(data) + 1;
	const resolved = { ...data };
	const getCompanyId = () => { for (let j = 0; j < top; j++) if (data[`field-${j}`] === 'company_id') { const v = resolved[`value-${j}`] ?? data[`value-${j}`]; return v && (UUID_REGEX.test(String(v)) ? v : companies.find(x => (x.nick || '') === v || (x.name || '') === v || String(x.nick || '') === v || String(x.name || '') === v)?.id); } return null; };
	for (let i = 0; i < top; i++)
	{
		const field = data[`field-${i}`];
		const val = data[`value-${i}`];
		if (!field || val == null || val === '')
			continue;
		const spec = fieldspecs.find(s => s.field === field);
		if (spec?.store === 'companies' && UUID_REGEX.test(String(val)))
		{
			const c = companies.find(x => x.id === val || String(x.id) === val);
			if (c?.[spec.labels?.[0]])
				resolved[`value-${i}`] = c[spec.labels[0]];
		}
		else if (spec?.store && spec.store !== 'companies' && spec.store !== 'locations' && spec.labels?.length && /^\d+$/.test(String(val)))
		{
			// Resolução genérica id→label para stores escopados por empresa
			const scope = getCompanyId();
			let opts = await db.list(spec.store).catch(() => []);
			if (spec.store === 'warehouses')
				opts = warehousesActiveOnly(opts);
			const item = opts.find(o => (!scope || String(o.company_id) === String(scope)) && (String(o.id) === String(val)));
			if (item?.[spec.labels[0]])
				resolved[`value-${i}`] = item[spec.labels[0]];
		}
		else if (spec?.store === 'locations' && field === 'location_id' && /^\d+:\d+$/.test(String(val)))
		{
			const [wh, id] = String(val).split(':');
			const loc = locations.find(l => String(l.warehouse_id) === wh && String(l.id) === id);
			if (loc)
				resolved[`value-${i}`] = `${loc.warehouse_id} - ${fmtLocAddr(loc.address)}`;
		}
	}
	return resolved;
};

export const resolveFilterLabelsToIds = async function(data, fieldspecs)
{
	if (!data || !fieldspecs)
		return data;
	let companies = await db.list('companies').catch(() => []);
	// Se companies vazio mas há company_id com label para resolver, buscar da API (db pode estar vazio antes de loadBaseData)
	const needsCompanyResolve = (() => {
		const top = getTopIndex(data) + 1;
		for (let i = 0; i < top; i++) {
			const f = data[`field-${i}`], v = data[`value-${i}`];
			if (f && v != null && v !== '' && fieldspecs.some(s => s.field === f && s.store === 'companies') && !UUID_REGEX.test(String(v).trim()))
				return true;
		}
		return false;
	})();
	if (companies.length === 0 && needsCompanyResolve && api.list_companies) {
		companies = await api.call(api.list_companies, '').catch(() => []);
		if (companies.length) db.set('companies', companies);
	}
	const locations = await db.list('locations').catch(() => []);
	const top = getTopIndex(data) + 1;
	const resolved = { ...data };
	for (let i = 0; i < top; i++)
	{
		const field = data[`field-${i}`];
		let val = data[`value-${i}`];
		if (!field || val == null || val === '')
			continue;
		val = String(val).trim();
		const spec = fieldspecs.find(s => s.field === field);
		if (spec?.store === 'companies' && !UUID_REGEX.test(val))
		{
			const labelKey = spec.labels?.[0] || 'nick';
			const match = (x) => (x[labelKey] || '') === val || String(x[labelKey] || '') === val || (x.name || '') === val || String(x.name || '') === val || (x.nick || '') === val || String(x.nick || '') === val;
			const c = companies.find(match);
			if (c?.id)
				resolved[`value-${i}`] = c.id;
		}
	}
	const getCompanyId = () => { for (let j = 0; j < top; j++) if (data[`field-${j}`] === 'company_id') { const v = resolved[`value-${j}`] ?? data[`value-${j}`]; return v && (UUID_REGEX.test(String(v)) ? v : companies.find(x => (x.nick || '') === v || (x.name || '') === v || String(x.nick || '') === v || String(x.name || '') === v)?.id); } return null; };
	for (let i = 0; i < top; i++)
	{
		const field = data[`field-${i}`];
		let val = data[`value-${i}`];
		if (!field || val == null || val === '')
			continue;
		val = String(val).trim();
		const spec = fieldspecs.find(s => s.field === field);
		const opLoc = String(data[`operator-${i}`] || 'eq').toLowerCase();
		if (spec?.store === 'locations' && field === 'location_id' && LOCATION_TEXT_FILTER_OPS.includes(opLoc))
			continue;
		if (spec?.store === 'locations' && field === 'location_id' && !/^\d+:\d+$/.test(val))
		{
			// Label "warehouse_id - address" ou apenas "address"
			const match = val.match(/^(\d+)\s*-\s*(.+)$/);
			const wh = match ? match[1] : null;
			const addrPart = match ? match[2].trim() : val;
			const addrNorm = addrPart.replace(/\s+/g, '-').toUpperCase();
			const scopeCompany = getCompanyId();
			const loc = locations.find(l =>
			{
				const a = fmtLocAddr(l.address ?? l.id);
				const addrMatch = a.replace(/\s+/g, '-').toUpperCase() === addrNorm || a.toUpperCase().includes(addrNorm);
				if (!addrMatch) return false;
				if (scopeCompany && String(l.company_id) !== String(scopeCompany)) return false;
				if (wh && String(l.warehouse_id) !== wh) return false;
				return true;
			});
			if (loc)
				resolved[`value-${i}`] = `${loc.warehouse_id}:${loc.id}`;
		}
		else if (spec?.store === 'customers' && field === 'customer_id')
		{
			const scope = getCompanyId();
			const opts = await db.list('customers').catch(() => []);
			const digits = val.replace(/\D/g, '');
			const normDoc = (d) => (d == null ? '' : String(d).replace(/\D/g, ''));
			let item = null;
			if (digits.length === 14)
				item = opts.find(o => (!scope || String(o.company_id) === String(scope)) && normDoc(o.cnpj) === digits);
			if (!item && digits.length === 11)
				item = opts.find(o => (!scope || String(o.company_id) === String(scope)) && normDoc(o.cpf) === digits);
			if (item?.id != null)
				resolved[`value-${i}`] = item.id;
			else if (!/^\d+$/.test(val))
			{
				const labelKey = spec.labels[0];
				item = opts.find(o => (!scope || String(o.company_id) === String(scope)) && ((o[labelKey] || '') === val || String(o[labelKey] || '') === val));
				if (item?.id != null)
					resolved[`value-${i}`] = item.id;
			}
		}
		else if (spec?.store && spec.store !== 'companies' && spec.labels?.length && !/^\d+$/.test(val) && !/^\d+:\d+$/.test(val))
		{
			// Resolução genérica para stores escopados por empresa (warehouses, programs, units, products, etc.)
			const scope = getCompanyId();
			let opts = await db.list(spec.store).catch(() => []);
			if (spec.store === 'warehouses')
				opts = warehousesActiveOnly(opts);
			const labelKey = spec.labels[0];
			const item = opts.find(o => (!scope || String(o.company_id) === String(scope)) && ((o[labelKey] || '') === val || String(o[labelKey] || '') === val));
			if (item?.id != null)
				resolved[`value-${i}`] = item.id;
		}
	}
	return resolved;
};

const updateTypes = async function(ev, publisher, field_name)
{
	let basespec = filter_form.fields.find(field => field.properties.selector == '#value-0');
	let fieldspec = this.fields.find(entry => entry.field == field_name);	// 'field_name' may be -1 when moving between rows
	let typed_field = field.create(basespec.properties, basespec.types.concat(fieldspec?.types || []));
	let index = publisher.selector.match(REGEX)[3];
	let current_field = this.formset.form.getFieldBySelector(replaceIndex('value-0', index));
	Object.assign(typed_field, { selector: current_field.selector });
	typed_field.releaseEvents();
	let node = document.querySelector(typed_field.selector);
	if (fieldspec?.placeholder)
		node.setAttribute('placeholder', fieldspec.placeholder);
	if (fieldspec?.title)
		node.setAttribute('title', fieldspec.title);
	if (fieldspec?.store)
	{
		let options = await db.list(fieldspec.store);
		if (fieldspec.store === 'warehouses')
			options = warehousesActiveOnly(options);
		// MT-596E375: filtrar por company_id/warehouse_id, ordenar por address, value = warehouse_id:location_id
		if (fieldspec.store === 'locations' && (fieldspec.field === 'location_id' || field_name === 'location_id'))
		{
			let companyId = null;
			let warehouseId = null;
			const formData = this.formset?.form?.getValues?.() || {};
			const topIdx = getTopIndex(formData) + 1;
			const [companies, warehousesRaw] = await Promise.all([
				db.list('companies').catch(() => []),
				db.list('warehouses').catch(() => [])
			]);
			const warehouses = warehousesActiveOnly(warehousesRaw);
			for (let i = 0; i < topIdx; i++)
			{
				if (formData[`field-${i}`] === 'company_id' && formData[`value-${i}`])
				{
					const val = formData[`value-${i}`];
					companyId = UUID_REGEX.test(String(val)) ? val : null;
					if (!companyId)
					{
					const c = companies.find(x => (x.nick || '') === val || (x.name || '') === val || String(x.nick || '') === val || String(x.name || '') === val);
					companyId = c?.id;
					}
				}
				else if (formData[`field-${i}`] === 'warehouse_id' && formData[`value-${i}`] != null && formData[`value-${i}`] !== '')
				{
					const wval = formData[`value-${i}`];
					warehouseId = /^\d+$/.test(String(wval)) ? (typeof wval === 'number' ? wval : parseInt(wval, 10)) : null;
					if (warehouseId == null && companyId)
					{
						const wh = warehouses.find(w => String(w.company_id) === String(companyId) && ((w.name || '') === wval || String(w.name || '') === wval));
						if (wh)
							warehouseId = wh.id;
					}
				}
			}
			options = options.map(loc => {
				const addr = fmtLocAddr(loc.address ?? loc.id);
				return {
					...loc,
					_wh_loc: `${loc.warehouse_id}:${loc.id}`,
					_label: `${loc.warehouse_id} - ${addr}`,
					_addrSort: addr
				};
			});
			// Excluir posições de armazéns inativos (IDB pode ter dados velhos de armazéns inativados).
			const activeWhKeys = new Set(warehouses.map((w) => `${w.company_id}:${w.id}`));
			if (activeWhKeys.size > 0)
				options = options.filter((l) => activeWhKeys.has(`${l.company_id}:${l.warehouse_id}`));
			if (companyId)
				options = options.filter(l => String(l.company_id) === String(companyId));
			if (warehouseId != null)
				options = options.filter(l => Number(l.warehouse_id) === Number(warehouseId));
			options.sort((a, b) => (a._addrSort || '').localeCompare(b._addrSort || '', undefined, { numeric: true }));
			loadOptions(typed_field.selector, options, '_wh_loc', ['_label']);
		}
		else if (fieldspec.store !== 'companies' && fieldspec.store !== 'locations' && options.length > 0
			&& (fieldspec.store === 'warehouses' || options[0].company_id != null))
		{
			// Stores escopados por empresa: warehouses, programs, units, classes, products, etc.
			const formData = this.formset?.form?.getValues?.() || {};
			const top = getTopIndex(formData) + 1;
			const companyId = await getCompanyIdFromFormData(formData, top);
			if (companyId)
				options = options.filter(o => String(o.company_id) === String(companyId));
			// Inativos (produtos, etc.): armazém já veio filtrado em warehousesActiveOnly no início.
			if (fieldspec.store !== 'warehouses' && options.length > 0 && options[0].is_disabled != null)
				options = options.filter(o => !o.is_disabled);
			loadOptions(typed_field.selector, options, fieldspec.value || 'id', fieldspec.labels, fieldspec.separator);
		}
		else
		{
			// Empresas: usar id como value (enviar UUID à API), nick como label (exibição)
			if (fieldspec.store === 'companies' && fieldspec.labels?.[0])
			{
				const labelKey = fieldspec.labels[0];
				const seen = new Set();
				options = options.filter(o => {
					const k = o.id ?? '';
					if (seen.has(k)) return false;
					seen.add(k);
					return true;
				});
				loadOptions(typed_field.selector, options, 'id', [labelKey]);
			}
			else
				loadOptions(typed_field.selector, options, fieldspec.value, fieldspec.labels, fieldspec.separator);
		}
	}
	else
	{
		node?.removeAttribute('list');
	}
	typed_field.captureEvents();
	typed_field.refresh();
};

const LOCATIONS_FIELDS = ['company_id', 'warehouse_id', 'location_id'];

const SCOPE_FIELDS = ['company_id', 'warehouse_id'];

/** Converte valor digitado na máscara pt-BR (ex.: 2.530) para literal numérico da API. */
function normalizeFilterValueForFieldspec(field, raw, fieldspecs)
{
	if (raw == null || raw === '' || !fieldspecs)
		return raw;
	const spec = fieldspecs.find(s => s.field === field);
	const numType = spec?.types?.find(t => t && typeof t.dp === 'number');
	if (!numType)
		return raw;
	let s = String(raw).trim().replace(/\s/g, '');
	const dp = numType.dp;
	if (dp === 0)
	{
		if (/,/.test(s))
			return raw;
		const cleaned = s.replace(/\./g, '');
		return /^\d+$/.test(cleaned) ? cleaned : raw;
	}
	if (/,/.test(s))
		return s.replace(/\./g, '').replace(',', '.');
	if (/^\d{1,3}(\.\d{3})+$/.test(s.replace(/^-/, '')))
		return s.replace(/\./g, '');
	return raw;
}

const translate = function(data, allowedFields = null, fieldspecs = null)
{
	let expression = '';
	let top_index = getTopIndex(data) + 1;
	let wrapOrGroup = false;
	const isNullOp = (op) => op === 'nu' || op === 'nn';
	const willOutput = (i) =>
	{
		const f = data[`field-${i}`];
		const v = data[`value-${i}`];
		const op = data[`operator-${i}`];
		if (!f)
			return false;
		if (!isNullOp(op) && (v == null || v === ''))
			return false;
		if (allowedFields && !allowedFields.includes(f))
			return false;
		if (f === 'location_id')
			return typeof v === 'string' && v.trim() !== '';
		return true;
	};

	for (let index = 0; index < top_index; index++)
	{
		if (!willOutput(index))
			continue;

		let open = data[`open-${index}`] == 1 ? '(' : '';
		let field = data[`field-${index}`];
		let operator = data[`operator-${index}`];
		let value = isNullOp(operator)
			? (data[`value-${index}`] != null && String(data[`value-${index}`]).trim() !== ''
				? String(data[`value-${index}`]).trim()
				: '0')
			: normalizeFilterValueForFieldspec(field, data[`value-${index}`], fieldspecs);
		let close = data[`close-${index}`] == 1 ? ')' : '';
		let connector = data[`connector-${index}`];
		const hasNext = () => { for (let j = index + 1; j < top_index; j++) if (willOutput(j)) return true; return false; };
		const restContainsOr = () => { for (let j = index + 1; j < top_index; j++) if (willOutput(j) && data[`connector-${j}`] === ';') return true; return false; };

		// Posição: texto (co/bw/ew…) → location_address; igual/diferente com par wh:id → dois campos inteiros; senão → texto no endereço (rótulo não resolvido).
		if (field === 'location_id')
		{
			const opLower = String(operator || 'eq').toLowerCase();
			if (LOCATION_TEXT_FILTER_OPS.includes(opLower))
			{
				const search = stripWarehousePrefixFromLocationSearch(value);
				expression += `${open}location_address*${operator}*${search}${close}`;
			}
			else if (/^\d+:\d+$/.test(String(value).trim()))
			{
				let [wh, loc] = String(value).split(':');
				expression += `${open}warehouse_id*${operator}*${wh},location_id*${operator}*${loc}${close}`;
			}
			else
			{
				const search = stripWarehousePrefixFromLocationSearch(value);
				expression += `${open}location_address*${operator}*${search}${close}`;
			}
		}
		else
			expression += `${open}${field}*${operator}*${value}${close}`;

		if (connector && connector !== -1 && hasNext())
		{
			// Empresa/Armazém seguido de cláusulas com "Ou": forçar AND(escopo, OR(resto))
			if (SCOPE_FIELDS.includes(field) && restContainsOr() && !wrapOrGroup)
			{
				expression += ',(';
				wrapOrGroup = true;
			}
			else
				expression += connector;
		}
		else if (wrapOrGroup)
		{
			expression += ')';
			wrapOrGroup = false;
		}
	}
	if (wrapOrGroup)
		expression += ')';
	return expression;
};

const filter = {

	fields: null,
	formset: null,

	init(fields)	// [{ field: '', label: '', types: [], usage: '' }, ...]
	{
		this.fields = fields;
		let options = makeOptions(fields, 'field', ['label']);
		filter_form.html = filter_form.html.replace(DATALIST, '$1' + options + '$3');
	},

	edit(callback, current_data, options = {})
	{
		this.reloadLocations = options.reloadLocations || null;
		filter_form.form.init = formInit.bind(this);
		filter_form.form.check = formCheck.bind(this);
		this.formset = formset.create(filter_form, async (updated_data) =>
		{
			this.formset.destroy();
			const resolved = await resolveFilterLabelsToIds(updated_data, this.fields);
			if (callback)
				await Promise.resolve(callback(resolved));
		});
		this.formset.init = (async () =>
		{
			this.formset.form.eraseValues();
			Object.getPrototypeOf(this.formset).init.call(this.formset);
			let rows = Object.keys(current_data || {}).filter(key => key.startsWith('connector')).length;
			this.formset.appendRows(Math.max(rows, 4) + 1);
			if (current_data)
			{
				const forDisplay = await resolveFilterIdsToLabels(current_data, this.fields);
				this.formset.form.setValues(forDisplay);
			}
			this.formset.form.refresh();
			setupFilterCsvImport(this);
		})();
	},

};

export const translateForLocations = (data, fieldspecs = null) => translate(data, LOCATIONS_FIELDS, fieldspecs);

export { filter as default, translate };

